import React, { useMemo, useState } from "react"

import ReactMarkdown from "react-markdown"

import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"

import {
  Button,
  DialogContent,
  DialogTitle,
  styled,
  Typography
} from "@mui/material"

import { ZoomInRounded } from "@mui/icons-material"

import HKVDialog from "../../HKVDialog/7.0"

import { isValidHttpUrl } from "../../../lib/utils"

import "./HKVMarkdown.css"

const StyledSpan = styled("span", {
})(({ theme }) => {
  return {
    display: "flex",
    width: "fit-content",
    height: "fit-content",
    position: "relative",
    margin: theme.spacing(2, 0),
    "& img": {
      minHeight: `calc(28px + ${theme.spacing(2)})`
    },
    "& .zoom-icon": {
      width: 28,
      height: 28,
      background: "rgba(0,0,0,0.5)",
      borderRadius: "50%",
      position: "absolute",
      right: theme.spacing(1),
      bottom: theme.spacing(1),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      color: "#fff"
    }
  }
})

const StyledDetails = styled("details")(({ theme }) => ({
  margin: theme.spacing(2, 0),
  "& > summary": {
    cursor: "pointer",
    color: theme.palette.lintblauw?.main || "#0066cc",
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 500,
    padding: theme.spacing(1, 0),
    listStyle: "none",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    "&::-webkit-details-marker": {
      display: "none"
    },
    "&::before": {
      content: '"▶"',
      fontSize: "0.7em",
      transition: "transform 0.2s"
    }
  },
  "&[open] > summary::before": {
    transform: "rotate(90deg)"
  },
  "& > *:not(summary)": {
    paddingLeft: theme.spacing(2),
    marginTop: theme.spacing(1)
  }
}))

const createKey = (text) => {
  return text?.trim()?.replaceAll(" ", "_")?.replaceAll("?", "")?.toLowerCase()
}

const createId = (data) => {
  if (Array.isArray(data)) {
    return data.map((d) => createId(d))
  } else if (typeof data === "string") {
    return createKey(data)
  } else {
    if (data?.hasOwnProperty("props")) {
      return data.props.children
    } else {
      return
    }
  }
}

const HKVMarkdown = ({ source, ...rest }) => {
  const [dialogConfig, setDialogConfig] = useState()

  const openDialog = (size, options) => {
    const mergedOptions = { ...options, open: true, size: size ? size : "lg" }
    setDialogConfig(mergedOptions)
  }

  const closeDialog = () => {
    setDialogConfig()
  }

  const renderImageDialog = () => {
    if (!dialogConfig) return

    const { imgSrc } = dialogConfig

    return (
      <HKVDialog
        config={dialogConfig}
        onClose={closeDialog}
      >
        <DialogTitle>Afbeelding</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
          <img
            title={imgSrc}
            alt={imgSrc}
            src={imgSrc}

            style={{ height: "50%", flexGrow: 1, objectFit: "contain" }}
          />
          <div className="dialog-actions">
            <Button
              variant="contained"
              className="dialog-button"
              onClick={closeDialog}
            >
              Ok
            </Button>
          </div>
        </DialogContent>
      </HKVDialog>
    )
  }

  const renderedMarkdown = useMemo(() => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        details: ({ node, ...rest }) => <StyledDetails {...rest} />,
        summary: ({ node, ...rest }) => <summary {...rest} />,
        table: ({ node, ...rest }) => {
          return <div
            style={{
              width: "100%",
              maxWidth: "100vw",
              overflowX: "auto",
              overflowY: "hidden"
            }}
          >
            <table {...rest} />
          </div>
        },
        img: ({ src, alt, node, ...rest }) => {
          const formattedSrc = isValidHttpUrl(src)
            ? src
            : `/static/images/theme_images/${src}`

          return (
            <StyledSpan onClick={() => openDialog("md", { imgSrc: formattedSrc })}>
              <img
                src={formattedSrc}
                title={alt}
                alt={alt}
                {...rest}
              />
              <span className="zoom-icon">
                <ZoomInRounded />
              </span>
            </StyledSpan>
          );
        },
        a: ({ href, title, ...rest }) => {
          return <a href={href} title={createId(rest.children)} target="_blank" {...rest} />;
        },
        p: ({ node, ...rest }) => <Typography {...rest} />,
        li: ({ node, ...rest }) => <li><Typography {...rest} /></li>,
        h1: ({ node, ...rest }) => <Typography {...rest} id={createId(rest.children)} variant="h1" color="lintblauw" />,
        h2: ({ node, ...rest }) => <Typography {...rest} id={createId(rest.children)} variant="h2" color="lintblauw" />,
        h3: ({ node, ...rest }) => <Typography {...rest} id={createId(rest.children)} variant="h3" color="lintblauw" />,
        h4: ({ node, ...rest }) => <Typography {...rest} id={createId(rest.children)} variant="h4" color="lintblauw" />,
        h5: ({ node, ...rest }) => <Typography {...rest} id={createId(rest.children)} variant="h5" color="lintblauw" />,
        h6: ({ node, ...rest }) => <Typography {...rest} id={createId(rest.children)} variant="h6" color="lintblauw" />
      }}
      children={source}
    />
  ), [source])

  return (
    <React.Fragment>
      <div {...rest}>
        {renderedMarkdown}
      </div>
      {renderImageDialog()}
    </React.Fragment>
  )
}

export default React.memo(HKVMarkdown)